import * as types from '../types';
import { ProductCategory, series3PlusCollarId } from '../types/Product';
import { getProductsBySku } from './product';

export const HARDWARE_FIRST_GIFT_TERMS_MONTHS = [6, 12, 24] as const;
export type HardwareFirstGiftTermMonths = (typeof HARDWARE_FIRST_GIFT_TERMS_MONTHS)[number];
export const DEFAULT_GIFT_TERM_MONTHS: HardwareFirstGiftTermMonths = 6;

export function giftTermOf(cart: types.Cart): HardwareFirstGiftTermMonths | undefined {
  return 'giftTermMonths' in cart
    ? HARDWARE_FIRST_GIFT_TERMS_MONTHS.find((months) => months === cart.giftTermMonths)
    : undefined;
}

/** web's gift cards sold by amount in the cart's currency, as opposed to the /gift page's plan gift cards */
export const isAmountGiftCardSku = (sku: string) => sku.startsWith('gift-card-amount-');

export const hasAmountGiftCard = (cart: types.Cart) =>
  Object.values(cart.cartItems).some((item) => isAmountGiftCardSku(item.lineItem.sku));

/** nothing in the cart ships, and nothing takes a code */
export const isAmountGiftCardOnlyCart = (cart: types.Cart) =>
  hasAmountGiftCard(cart) && Object.values(cart.cartItems).every((item) => isAmountGiftCardSku(item.lineItem.sku));

/** a gift or an amount card needs recipient and delivery fields that only the stripe checkout page collects */
export const cartNeedsGiftDetails = (cart: types.Cart) =>
  ('isGift' in cart && !!cart.isGift) || hasAmountGiftCard(cart);

export function giftHardwareSkuForTerm(
  hardwareSku: string,
  termMonths: HardwareFirstGiftTermMonths,
  products: types.IProduct[],
): string | undefined {
  const product = getProductsBySku(products).get(hardwareSku);
  if (!product) {
    return undefined;
  }

  const months = termMonths.toString().padStart(2, '0');

  // web's modifiedSkuForPrepaidSubscription: <sku>-[W]<MM>M; the series 3+ gifts unwrapped on every term
  if (product.id === series3PlusCollarId) {
    return `${hardwareSku}-${months}M`;
  }

  if (product.category === ProductCategory.MINI) {
    const option = product.deferredSubscriptionOptions?.find(({ renewalMonths }) => renewalMonths === termMonths);
    return option ? `${product.sku}-${option.giftWrapped ? 'W' : ''}${months}M` : undefined;
  }

  return undefined;
}

export interface OfferedGiftPlanTerm {
  months: HardwareFirstGiftTermMonths;
  introAmountInCents: number;
  listAmountInCents: number;
}

export function offeredGiftPlanTerms(terms: types.GiftPlanTermPricing[]): OfferedGiftPlanTerm[] {
  return HARDWARE_FIRST_GIFT_TERMS_MONTHS.flatMap((months) => {
    const term = terms.find((priced) => priced.months === months);
    return term
      ? [{ months, introAmountInCents: term.introAmountInCents, listAmountInCents: term.listAmountInCents }]
      : [];
  });
}

/** one renewal price for the sheet to quote; undefined when web has none or the trackers renew at different prices. */
export function sharedGiftRenewalPriceInCents(
  prices: NonNullable<types.CartPricing['giftRenewalPrices']>,
): number | undefined {
  const amounts = new Set(Object.values(prices).map(({ renewalAmountInCents }) => renewalAmountInCents));
  return amounts.size === 1 ? [...amounts][0] : undefined;
}

/** one gifted tracker's prepaid plan, off web's `hf-gift-plan-*` lines: one term per order, so every tracker's is the same */
export function giftPlanPerTrackerInCents(
  lineItems: types.CartPricing['lineItems'],
  trackerUnits: number,
): number | undefined {
  const planLines = (lineItems ?? []).filter(({ sku }) => sku.startsWith('hf-gift-plan-'));
  if (planLines.length === 0 || trackerUnits === 0) {
    return undefined;
  }
  const planInDollars = planLines.reduce((sum, line) => sum + line.subtotalInDollars, 0);
  return Math.round((planInDollars * 100) / trackerUnits);
}
